import { answerKey } from "@gierki/shared";
import { Karta, Odznaka, Przycisk } from "../ui.tsx";
import type { PmView } from "./PanstwaMiasta.tsx";

/**
 * Wyniki rundy i tabela całej partii.
 *
 * Przy każdej odpowiedzi widać nie tylko liczbę punktów, ale i powód —
 * „powtórka", „zła litera", „odrzucone". To jest ekran, na którym rozstrzygają
 * się pretensje, więc musi tłumaczyć sam siebie bez pytania kogokolwiek.
 */

const OPIS_STATUSU: Record<string, string> = {
  blank: "puste",
  "wrong-letter": "zła litera",
  dictionary: "słownik",
  unchallenged: "bez sprzeciwu",
  accepted: "obronione",
  rejected: "odrzucone",
};

export function Wyniki({
  pm,
  nicki,
  onNastepna,
  onWroc,
}: {
  pm: PmView;
  nicki: Record<string, string>;
  onNastepna: () => void;
  onWroc: () => void;
}) {
  const koniec = pm.phase === "finished";

  const tabela = Object.entries(pm.totals)
    .map(([playerId, punkty]) => ({ playerId, punkty }))
    .sort((a, b) => b.punkty - a.punkty);

  const ostatniaRunda = pm.history[pm.history.length - 1];

  return (
    <>
      <Karta>
        <div className="text-sm text-tekst-slaby mb-3">
          {koniec ? "Wynik końcowy" : `Po ${pm.roundNo} z ${pm.roundCount} rund`}
        </div>
        <div className="space-y-2">
          {tabela.map((wiersz, miejsce) => (
            <div key={wiersz.playerId} className="flex items-center gap-3">
              <span className="w-6 text-tekst-slaby tabular">{miejsce + 1}.</span>
              <span className="flex-1 truncate">
                {nicki[wiersz.playerId] ?? "?"}
                {koniec && miejsce === 0 ? " 🏆" : ""}
              </span>
              {ostatniaRunda ? (
                <span className="text-sm text-akcent tabular">
                  +{ostatniaRunda.points[wiersz.playerId] ?? 0}
                </span>
              ) : null}
              <span className="text-xl font-semibold tabular w-14 text-right">{wiersz.punkty}</span>
            </div>
          ))}
        </div>
      </Karta>

      {pm.categories.map((kategoria) => {
        const wpisy = pm.evaluations.filter((wpis) => wpis.category === kategoria.key);
        if (wpisy.length === 0) return null;

        return (
          <Karta key={kategoria.key}>
            <div className="text-sm text-tekst-slaby mb-2">
              {kategoria.label} {pm.letter ? `na ${pm.letter}` : ""}
            </div>
            <div className="space-y-2">
              {wpisy.map((wpis) => (
                <div
                  key={answerKey(wpis.playerId, wpis.category)}
                  className="flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="text-xs text-tekst-slaby">{nicki[wpis.playerId] ?? "?"}</div>
                    <div className={wpis.points === 0 ? "text-tekst-slaby line-through" : ""}>
                      {wpis.display || "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {/* Ta sama zieleń co na ekranie sprzeciwów — „słownik"
                        znaczy wszędzie to samo: nikt tego nie kwestionował
                        i nie musiał. */}
                    <Odznaka zielona={wpis.status === "dictionary"}>
                      {OPIS_STATUSU[wpis.status] ?? wpis.status}
                    </Odznaka>
                    <span className="tabular w-8 text-right font-semibold">{wpis.points}</span>
                  </div>
                </div>
              ))}
            </div>
          </Karta>
        );
      })}

      <div className="sticky bottom-0 pt-2 bezpieczny-dol bg-tlo space-y-2">
        {koniec ? (
          <Przycisk wariant="glowny" pelna onClick={onWroc} className="min-h-16">
            Wróć do listy partii
          </Przycisk>
        ) : pm.canStart ? (
          <Przycisk wariant="glowny" pelna onClick={onNastepna} className="min-h-16">
            Następna runda
          </Przycisk>
        ) : (
          <div className="text-center text-tekst-slaby py-3">Czekamy na hosta…</div>
        )}
      </div>
    </>
  );
}
